import { useTranslation } from 'react-i18next';
import { riskReasonKey } from '@d-orca/shared/client/activities';
import { severityClassName, type IRiskScore, type ISafetyRisk, type RiskSeverity } from '../model/risk';
import styles from './riskAssessment.module.css';

// Severity → colour, the same mapping the Daily List risk badges use
// (RiskBadge.module.css) so the drawer and the cards read identically.
const TONE_CLASS: Record<RiskSeverity, string | undefined> = {
  none: styles.none,
  low: styles.low,
  mid: styles.mid,
  high: styles.high,
};

function RiskLine({ label, score }: { label: string; score: IRiskScore | ISafetyRisk }) {
  const { t } = useTranslation();
  const reasons = score.reasons.map((code) => t(riskReasonKey(code))).join(' · ');
  return (
    <div className={styles.line}>
      <div className={styles.value}>
        <span className={styles.label}>{label}</span>
        <span className={styles.dot} aria-hidden>·</span>
        <span className={`${styles.score} ${severityClassName(score.level, TONE_CLASS)}`}>{score.level}/5</span>
      </div>
      {reasons && <p className={styles.reason}>{reasons}</p>}
    </div>
  );
}

export interface IRiskAssessmentProps {
  onTime: IRiskScore;
  safety: ISafetyRisk;
}

/**
 * The activity drawer's Risk Assessment section: On-Time and Safety risk, each
 * scored /5 with the number tinted by severity. Rendered into the shared
 * TaskPanel's Overview tab via its `overviewSlot`, so the Scheduler stays
 * decoupled from the Activities risk model.
 */
export function RiskAssessment({ onTime, safety }: IRiskAssessmentProps) {
  const { t } = useTranslation();
  return (
    <section className={styles.section}>
      <h3 className={styles.title}>{t('activities.drawer.risk')}</h3>
      <RiskLine label={t('activities.drawer.onTimeRisk')} score={onTime} />
      <RiskLine label={t('activities.drawer.safetyRisk')} score={safety} />
    </section>
  );
}
